import { Icon } from '../../../components/icons/Icon'
import { StatusPill } from '../../../components/StatusPill'
import { lifecycleStatusPill } from '../cartridge-studio/lifecycleStatus'
import type { DecisionView, EligibilityModelView } from '../../../types/cartridgeStudio'

export interface CanonReferencePaneProps {
  model: EligibilityModelView
  decisions: DecisionView[]
}

/**
 * ADR-0056 §3.1 — canon is a READ-ONLY reference here, never an editable form shell (design doc §3.3).
 * No `iw-input`/editable affordance anywhere in this component, and no grayed-out-form-inviting-a-click
 * styling: plain read-only rows only, so a tenant author can never mistake this for "editing canon".
 * `OverlayDeltaEditor` operates on a COPY staged client-side; nothing here is ever written back.
 */
export function CanonReferencePane({ model, decisions }: CanonReferencePaneProps) {
  const pill = lifecycleStatusPill(model.status)
  return (
    <section aria-label="Canon reference (read-only)" className="iw-overlay-canon-pane">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', marginBottom: 'var(--iw-space-3)' }}>
        <Icon name="lock" size={14} />
        <strong style={{ fontWeight: 500 }}>Canon reference — read only</strong>
        <StatusPill {...pill} />
        <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
          EligibilityModel v{model.version}
        </span>
      </div>
      <p style={{ margin: '0 0 var(--iw-space-3)', color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
        This platform-owned content can never be edited from here. Your overlay is a separate,
        tenant-owned artifact that adds to, overrides, or removes from it — it never mutates these
        rows (ADR-0056).
      </p>
      {decisions.length === 0 ? (
        <div style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
          This canon model has no published decisions yet.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
          {decisions.map((d) => (
            <li
              key={d.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)',
                padding: 'var(--iw-space-2)', background: 'var(--iw-surface-raised)',
                borderRadius: 'var(--iw-radius-sm)',
              }}
            >
              <code>{d.key}</code>
              <StatusPill variant="neutral" label={d.kind} />
              {d.hitPolicy ? <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>{d.hitPolicy}</span> : null}
              {d.citationRef ? <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>{d.citationRef}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
